import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { correctPathname } from '../correctPathname';
import {
  REALTOR_RATING_AGENT_ID_QS_NAME,
  REALTOR_RATING_DEFAULT_PERIOD_VALUE,
  REALTOR_RATING_DEFAULT_REGION_VALUE,
  REALTOR_RATING_MIN_PERIOD,
  REALTOR_RATING_PERIOD_QS_NAME,
  REALTOR_RATING_REGION_QS_NAME,
} from '../../constants';
import { prepareRegion } from '../prepareRegion';
import { preparePeriod } from '../preparePeriod';
import { selectRegionsNew } from '../../selectors/geo/regionsNew';
import { prepareQSParam } from '../prepareQSParam';

/**
 * Сохраняет и считывает фильтры в/из адресной строки
 */
export const useRealtorRatingFilter = () => {
  const { search, pathname } = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const history = useHistory();
  const { items: regionItems } = useSelector(selectRegionsNew);
  const region =
    prepareRegion(
      searchParams.get(REALTOR_RATING_REGION_QS_NAME) || '',
      regionItems.map(item => item.id),
    ) || 0;
  const period =
    preparePeriod(
      searchParams.get(REALTOR_RATING_PERIOD_QS_NAME) || '',
      Array.from({ length: new Date().getFullYear() - REALTOR_RATING_MIN_PERIOD + 1 }, (_, index) =>
        String(REALTOR_RATING_MIN_PERIOD + index),
      ),
    ) || REALTOR_RATING_DEFAULT_PERIOD_VALUE;
  const agentId = Number(prepareQSParam(searchParams.get(REALTOR_RATING_AGENT_ID_QS_NAME) || ''));

  /** Устанавливает значение региона в адресную строку */
  const setRegion = useCallback(
    (region: number) => {
      if (region === REALTOR_RATING_DEFAULT_REGION_VALUE) {
        /** Удаляет дефолтное значение из qs */
        searchParams.delete(REALTOR_RATING_REGION_QS_NAME);
      } else {
        /** Обновляет значение из qs */
        searchParams.set(REALTOR_RATING_REGION_QS_NAME, String(region));
      }

      history.push(`${correctPathname(pathname)}?${searchParams.toString()}`);
    },
    [history, pathname, searchParams],
  );

  /** Устанавливает значение периода в адресную строку */
  const setPeriod = useCallback(
    (period: string) => {
      if (period === REALTOR_RATING_DEFAULT_PERIOD_VALUE) {
        /** Удаляет дефолтное значение из qs */
        searchParams.delete(REALTOR_RATING_PERIOD_QS_NAME);
      } else {
        /** Обновляет значение из qs */
        searchParams.set(REALTOR_RATING_PERIOD_QS_NAME, period);
      }

      history.push(`${correctPathname(pathname)}?${searchParams.toString()}`);
    },
    [history, pathname, searchParams],
  );

  return {
    region,
    period,
    setRegion,
    setPeriod,
    agentId,
  };
};
